/**
 * @module app.pp
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { ProviderService } from 'app/cd/services/provider-service';
import { UserService } from 'app/core/services/user-service';
import { NavigationService } from 'app/core/services/navigation-service';
import { LookupService } from 'app/core/services/lookup-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { deepClone } from 'app/shared/lib/utilities';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { guid } from 'app/shared/lib/guid';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';


type ProviderLocationWithLocalState = ExtendLocalState<CD.ProviderLocationUserDetailModel, { isSelected?: boolean; }>;
type UserContext = CD.ProviderUserInvitationContext | CD.ProviderUserContext;

@autoinject()
export class SaveContextCustomElement extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    private originalContext: UserContext | null = null;
    protected context: UserContext | null = null;
    protected locations: Array<ProviderLocationWithLocalState> | null = null;
    protected roles: Array<Core.UserRole> | null = null;
    protected filteredHomePages: ReadonlyArray<Core.NavigationHomePage> | null = null;
    protected timeZones: ReadonlyArray<Core.TimeZone> | null = null;
    private allHomePages: ReadonlyArray<Core.NavigationHomePage> | null = null;

    protected selectedLocations: Array<ProviderLocationWithLocalState> = [];
    protected selectedRoles: Array<Core.UserRole> = [];
    protected selectedHomePage: Core.NavigationHomePage | null = null;
    protected selectedTimeZone: Core.TimeZone | null = null;
    protected selectedEmailAddress: string | null = null;

    protected noneContact: CD.ProviderContact = { $display: 'None' }

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;

    protected matcher = modelIdMatcher;

    protected isInvitation: boolean = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly userService: UserService,
        private readonly navigationService: NavigationService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async bind(): Promise<void> {
        this.loadLookups();
    }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    private async loadLookups(): Promise<void> {
        Promise.all([
            this.loadHomePages(),
            this.loadTimeZones()
        ]);
    }

    public async loadInvitationContext(invitationContext: CD.ProviderUserInvitationContext | null): Promise<void> {
        this.reset();
        this.originalContext = invitationContext || {};
        this.context = deepClone(this.originalContext);

        this.isInvitation = true;

        await this.loadCommonData();
    }

    public async loadUserContext(invitationContext: CD.ProviderUserContext | null): Promise<void> {
        this.reset();
        this.originalContext = invitationContext || {};
        this.context = deepClone(this.originalContext);

        this.isInvitation = false;

        await this.loadCommonData();
    }

    private async loadCommonData(): Promise<void> {
        const [userLocations, userRoles, invitationLocations, invitationRoles] = await Promise.all(
            [
                !!this.locations ? this.locations : this.loadProviderUserLocations(),
                !!this.roles ? this.roles : this.loadProviderUserRoles(),
                !!this.context?.providerLocations ? this.context.providerLocations : [],
                !!this.context?.roles ? this.context.roles : []
            ]
        );

        this.matchLocations(userLocations, invitationLocations);

        this.selectedRoles.push(...invitationRoles);
        this.updateHomePages();

        this.locations = userLocations;
        this.roles = userRoles;

        const invitationContext: CD.ProviderUserInvitationContext | null = this.context;
        const context: CD.ProviderUserContext | null = this.context;

        this.selectedHomePage = invitationContext?.homePage || context?.homePage || null;
        this.selectedTimeZone = invitationContext?.invitation?.displayTimeZone || context?.user?.displayTimeZone || null;
        this.selectedEmailAddress = invitationContext?.invitation?.emailAddress || context?.user?.emailAddress || null;

    }

    /** Matches existing locations on an invitation to available locations and selects them */
    private matchLocations(userLocations: ProviderLocationWithLocalState[], invitationLocations: ProviderLocationWithLocalState[]): void {
        // Reset location detail and local state properties
        userLocations.forEach((location) => {
            location.detail = undefined;
            location._state = { isSelected: false };
        });

        /** Locations from the existing invitation are added to the selectedLocations array */
        invitationLocations.forEach((il) => {
            const location = userLocations.find((r) => r.id === il.id);
            if(location) {
                // If provider user location existed in invitation provider locations, select it
                location._state = { isSelected: true };
                location.detail = il.detail;
                this.selectedLocations.push(location);
            }
        });
    }

    protected updateLocations(): void {
        if(!this.locations) { return; }

        this.locations.forEach((l) => {
            if(this.selectedLocations.some((sl) => sl.id === l.id)) {
                l._state!.isSelected = true;
            } else {
                l._state = { isSelected: false };
            }
        });
    }

    /**Updates filteredHomePages to only home pages that exist for selectedRoles */
    protected updateHomePages(): void {
        if(!this.allHomePages) { return; }
        this.filteredHomePages = this.allHomePages.filter((page) => {
            return page.roles?.some((r) => this.selectedRoles.find((sr) => sr.id === r.id)) || page.flag?.allUsers;
        });
    }

    private buildSaveContext(): CD.ProviderUserContext | null {
        const id: string | null | undefined = this.context?.id;

        if(!id) {
            this.toaster.error().message('No user context available to save').show();
        }

        const saveContext: CD.ProviderUserContext = {
            id: id,

            providerLocations: getReferences(this.selectedLocations, (location) => (
                {
                    detail: !location.detail ? undefined :
                        {
                            associatedContact: getReference(location.detail?.associatedContact)
                        }
                })),
            roles: getReferences(this.selectedRoles),
            homePage: getReference(this.selectedHomePage)
        }

        return saveContext;
    }

    private buildSaveInvitation(): CD.ProviderUserInvitationContext | null {
        const providerId = this.selectedLocations[0].provider?.id;

        const invitationContext: CD.ProviderUserInvitationContext | null = this.context;

        const saveInvitation: CD.ProviderUserInvitationContext = {
            id: invitationContext?.id || undefined,
            invitation: {
                id: invitationContext?.invitation?.id || undefined,
                emailAddress: this.selectedEmailAddress,
                displayTimeZone: getReference(this.selectedTimeZone)
            },
            homePage: getReference(this.selectedHomePage),
            provider: { id: providerId },
            providerLocations: getReferences(this.selectedLocations, (location) => (
                {
                    detail: !location.detail ? undefined :
                        {
                            associatedContact: getReference(location.detail?.associatedContact)
                        }
                })),
            roles: getReferences(this.selectedRoles)
        }

        return saveInvitation;
    }


    ////****************** IForm ****************************************************////
    public reset(): void {
        this.originalContext = null;
        this.context = null;

        this.selectedLocations = [];
        this.selectedRoles = [];
        this.selectedHomePage = null;
        this.selectedTimeZone = null;
    }
    public hasChanges(): boolean {
        return JSON.stringify(this.context) !== JSON.stringify(this.originalContext);
    }
    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(this.isInvitation) {
            return this.saveInvitation();
        } else {
            return this.updateContext();
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadProviderUserLocations(): Promise<ProviderLocationWithLocalState[]> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting
        };
        const includes: string[] = ['facility', 'contacts', 'contacts.primaryUser'];

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadProviderLocationsBySession(options, includes);
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviderUserRoles(): Promise<Core.UserRole[]> {
        try {
            this.state.beginLoad();

            const response = await this.userService.loadUserRolesByApplication();
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadHomePages(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = ['roles'];

        try {
            this.state.beginLoad();

            const response = await this.navigationService.loadHomePages(options, includes);
            this.allHomePages = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTimeZones(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = [];

        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadTimeZones(options, includes);
            this.timeZones = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveInvitation(): Promise<boolean> {
        const saveInvitation = this.buildSaveInvitation();
        if(!saveInvitation) { return false; }

        try {
            this.state.beginSave();

            const response = await this.providerService.saveProviderUserInvitationContext(saveInvitation);
            this.toaster.standard.saved('pp:providerUserInvitation.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async updateContext(): Promise<boolean> {
        const saveContext = this.buildSaveContext();

        if(!saveContext) { return false; }

        try {
            this.state.beginSave();

            const response = await this.providerService.saveProviderUserContext(saveContext);
            this.toaster.standard.saved('core:user.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
